/**
 * React Query hook for the CRM unified timeline.
 */

import { useQuery } from '@tanstack/react-query';
import api, { CrmTimelineEvent } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

interface UseTimelineOptions {
  contactId?: string;
  companyId?: string;
  dealId?: string;
  limit?: number;
}

function buildParams(options: UseTimelineOptions): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (options.contactId) params.contactId = options.contactId;
  if (options.companyId) params.companyId = options.companyId;
  if (options.dealId) params.dealId = options.dealId;
  if (options.limit !== undefined) params.limit = options.limit;
  return params;
}

export function useTimeline(options: UseTimelineOptions = {}) {
  const filters = buildParams(options);

  const query = useQuery<{ events: CrmTimelineEvent[]; total: number }>({
    queryKey: queryKeys.crm.timeline(filters),
    queryFn: () => api.getCrmTimeline(Object.keys(filters).length > 0 ? filters : undefined),
  });

  if (query.isError && !query.isFetching) {
    toast({ title: 'Error', description: 'Failed to load timeline', variant: 'destructive' });
  }

  return {
    events: query.data?.events ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}
